import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getFirestore, doc, updateDoc, deleteField, writeBatch } from 'firebase/firestore';

/**
 * Updates the in-cache leads array for every active leads query so optimistic
 * updates show up everywhere the data is rendered. Returns a snapshot the
 * caller can use to roll back on error.
 */
function patchAllLeadCaches(queryClient, companyId, predicate, patcher) {
    const snapshots = [];
    // Match both leads and leadsBootstrap caches.
    const matches = queryClient.getQueriesData({ predicate: (q) => {
        const k = q.queryKey;
        if (!Array.isArray(k) || k.length < 2) return false;
        if (k[0] !== 'leads' && k[0] !== 'leadsBootstrap') return false;
        return k[1] === companyId;
    }});
    for (const [key, value] of matches) {
        snapshots.push([key, value]);
        if (!value) continue;
        // leadsBootstrap nests the leads payload under .leads
        const bag = value.leads ? { ...value, leads: { ...value.leads, data: [...(value.leads.data || [])] } }
                                : { ...value, data: [...(value.data || [])] };
        const arr = bag.leads ? bag.leads.data : bag.data;
        for (let i = 0; i < arr.length; i++) {
            if (!predicate(arr[i]?.data)) continue;
            const patched = patcher(arr[i]?.data);
            if (patched === undefined) continue;
            arr[i] = { ...arr[i], data: patched };
        }
        queryClient.setQueryData(key, bag);
    }
    return snapshots;
}

function rollback(queryClient, snapshots) {
    for (const [key, value] of snapshots) queryClient.setQueryData(key, value);
}

/**
 * Archive / restore — straightforward optimistic Firestore update.
 *
 * Canonical field set — must stay in lockstep with Leads.js
 * handleArchiveLead/handleRestoreLead/handleUndoArchive until those migrate
 * here: archive sets isArchived/archivedBy/archivedAt, restore deletes all
 * three, both touch updatedAt. Analytics events stay at the call site
 * because their semantics differ per surface (undo vs deliberate restore).
 */
export function useArchiveLead({ companyId }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ leadId, archive, archivedBy = null }) => {
            const db = getFirestore();
            const ref = doc(db, 'companies', companyId, 'leads', leadId);
            const now = new Date();
            await updateDoc(ref, archive ? {
                'data.isArchived': true,
                'data.archivedBy': archivedBy,
                'data.archivedAt': now,
                'data.updatedAt': now,
            } : {
                'data.isArchived': deleteField(),
                'data.archivedBy': deleteField(),
                'data.archivedAt': deleteField(),
                'data.updatedAt': now,
            });
        },
        onMutate: async ({ leadId, archive }) => {
            const snapshots = patchAllLeadCaches(
                queryClient,
                companyId,
                (lead) => lead?.leadId === leadId,
                (lead) => ({ ...lead, isArchived: archive ? true : false })
            );
            return { snapshots };
        },
        onError: (_err, _vars, ctx) => ctx && rollback(queryClient, ctx.snapshots),
        // The map cache (['leadsInBounds', ...]) has a row shape patchAllLeadCaches
        // doesn't understand, so it only reconciles via invalidation.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['leads', companyId], exact: false });
            queryClient.invalidateQueries({ queryKey: ['leadsBootstrap', companyId], exact: false });
            queryClient.invalidateQueries({ queryKey: ['leadsInBounds', companyId], exact: false });
        }
    });
}

/**
 * Per-row inline edits get queued and flushed every 500ms in a single
 * Firestore writeBatch. Multiple users editing different rows still benefit;
 * the same user editing five cells across two rows lands in one round-trip.
 *
 * We keep the queue at module scope (per company) so multiple cell components
 * mounting/unmounting during rapid edits don't lose pending writes.
 */
const FLUSH_MS = 500;
const queues = new Map(); // companyId -> { pending: Map<leadId, fields>, timer, queryClient }

function getQueue(companyId, queryClient) {
    let q = queues.get(companyId);
    if (!q) {
        q = { pending: new Map(), timer: null, queryClient };
        queues.set(companyId, q);
    } else {
        q.queryClient = queryClient;
    }
    return q;
}

async function flushQueue(companyId) {
    const q = queues.get(companyId);
    if (!q || q.pending.size === 0) return;
    const pending = q.pending;
    q.pending = new Map();
    q.timer = null;

    const db = getFirestore();
    const batch = writeBatch(db);
    for (const [leadId, fields] of pending.entries()) {
        const ref = doc(db, 'companies', companyId, 'leads', leadId);
        // Each entry's `fields` is a flat dotted-path → value map (e.g. 'data.dealStatus')
        batch.update(ref, fields);
    }
    try {
        await batch.commit();
    } catch (err) {
        console.error('[useLeadMutation] batch flush failed', err);
        // On failure, re-queue so retry can pick them up. We don't currently
        // surface this to the UI — the optimistic patch already rendered, so
        // the failure mode is "the UI thinks it saved, server didn't". A
        // future toast layer should consume this.
        for (const [leadId, fields] of pending.entries()) {
            const merged = q.pending.get(leadId) || {};
            q.pending.set(leadId, { ...fields, ...merged });
        }
        scheduleFlush(companyId, q.queryClient);
        return;
    }
    if (q.queryClient) {
        // Stale — refetch in the background to reconcile any server-side
        // computed fields (e.g. updatedAt, computedSpread).
        q.queryClient.invalidateQueries({ queryKey: ['leads', companyId], exact: false });
    }
}

function scheduleFlush(companyId, queryClient) {
    const q = getQueue(companyId, queryClient);
    if (q.timer) return;
    q.timer = setTimeout(() => flushQueue(companyId), FLUSH_MS);
}

/**
 * Inline edit a single field on a lead. Optimistically patches all caches,
 * queues the Firestore write to be batched with other edits in the same window.
 *
 * `field` is a dotted path under the lead doc, e.g. 'data.dealStatus' or
 * 'data.verifiedCharacteristics.bedrooms'.
 */
export function useEditLeadField({ companyId }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ leadId, field, value }) => {
            const q = getQueue(companyId, queryClient);
            const existing = q.pending.get(leadId) || {};
            existing[field] = value;
            q.pending.set(leadId, existing);
            scheduleFlush(companyId, queryClient);
        },
        onMutate: async ({ leadId, field, value }) => {
            // Walk the dotted path on the cached lead and apply the value.
            const snapshots = patchAllLeadCaches(
                queryClient,
                companyId,
                (lead) => lead?.leadId === leadId,
                (lead) => {
                    const parts = field.startsWith('data.') ? field.slice(5).split('.') : field.split('.');
                    const next = JSON.parse(JSON.stringify(lead));
                    let cursor = next;
                    for (let i = 0; i < parts.length - 1; i++) {
                        const k = parts[i];
                        if (cursor[k] == null || typeof cursor[k] !== 'object') cursor[k] = {};
                        cursor = cursor[k];
                    }
                    cursor[parts[parts.length - 1]] = value;
                    return next;
                }
            );
            return { snapshots };
        },
        onError: (_err, _vars, ctx) => ctx && rollback(queryClient, ctx.snapshots)
    });
}

/**
 * Plain (non-hook) escape hatch for components deep in the tree that don't
 * have easy access to the mutation hook. Same batching guarantees as
 * useEditLeadField — `fields` is a flat dotted-path → value object.
 *
 * Fire-and-forget: returns immediately after queuing. Failures are logged
 * and the matching ['leads', companyId, ...] queries are invalidated on
 * the next flush boundary so the UI eventually reconciles.
 *
 * @param {string} companyId
 * @param {string} leadId
 * @param {Object} fields  e.g. { 'data.dealStatus': 'lost', 'data.updatedAt': new Date() }
 * @param {QueryClient} [queryClient] — optional; pass when you want
 *   onSettled-style cache invalidation. Not required for the write itself.
 */
export function enqueueLeadEdit(companyId, leadId, fields, queryClient) {
    if (!companyId || !leadId || !fields) return;
    const q = getQueue(companyId, queryClient || (queues.get(companyId)?.queryClient));
    const existing = q.pending.get(leadId) || {};
    q.pending.set(leadId, { ...existing, ...fields });
    scheduleFlush(companyId, q.queryClient);
}

/**
 * Hook that flushes any pending edits when the page unmounts so leaving
 * doesn't lose in-flight writes.
 */
export function useFlushPendingLeadEdits(companyId) {
    const cidRef = useRef(companyId);
    cidRef.current = companyId;
    useEffect(() => {
        return () => {
            const cid = cidRef.current;
            if (cid) flushQueue(cid);
        };
    }, []);
}
